import React from 'react';
import styled from 'styled-components';
import { restaurantMenus, brasserieMenus, getMenuText } from '../config/menuTexts';
import { useLanguage } from '../context/LanguageContext';

// Placeholder image - à remplacer par les vraies photos plus tard
const restaurantImage = '/images/restaurant2.jpg';

const PageHeader = styled.div`
  height: 40vh;
  min-height: 300px;
  background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/images/restaurant.jpg');
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  margin-top: 70px; /* Pour laisser de l'espace pour le header fixe */
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  color: ${({ theme }) => theme.colors.white};
`;

const RestaurantSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
`;

const RestaurantContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const IntroSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const IntroImage = styled.div`
  height: 400px;
  background-image: url(${restaurantImage});
  background-size: cover;
  background-position: center;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 300px;
    order: 1;
  }
`;

const IntroContent = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    order: 2;
  }
`;

const IntroTitle = styled.h2`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 2rem;
  position: relative;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const IntroDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textLight};
`;

const MenusTitle = styled.h2`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  position: relative;
  padding-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 2rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const MenuCard = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  padding: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 5px;
    height: 100%;
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const MenuTitle = styled.h3`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.5rem;
`;

const MenuSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const MenuSectionTitle = styled.h4`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 1.2rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.light};
  padding-bottom: ${({ theme }) => theme.spacing.xs};
`;

const MenuItems = styled.ul`
  list-style: none;
  padding: 0;
`;

const MenuItem = styled.li`
  padding: ${({ theme }) => theme.spacing.sm} 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px dashed ${({ theme }) => theme.colors.light};
  
  &:last-child {
    border-bottom: none;
  }
`;

const MenuPrice = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.3rem;
  text-align: right;
  margin-top: ${({ theme }) => theme.spacing.md};
  padding-top: ${({ theme }) => theme.spacing.sm};
  border-top: 2px solid ${({ theme }) => theme.colors.accent};
`;

const DownloadButton = styled.a`
  display: inline-block;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  text-decoration: none;
  font-weight: 600;
  margin-top: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  transition: background-color ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
  }
  
  svg {
    margin-right: ${({ theme }) => theme.spacing.sm};
    vertical-align: middle;
  }
`;

const DownloadContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Restaurant = () => {
  const { t, language } = useLanguage();
  
  return (
    <>
      <PageHeader>
        <PageTitle>{t('restaurant.pageTitle')}</PageTitle>
      </PageHeader>
      
      <RestaurantSection>
        <RestaurantContainer>
          <IntroSection>
            <IntroContent>
              <IntroTitle>{t('restaurant.intro.title')}</IntroTitle>
              <IntroDescription>
                {t('restaurant.intro.description')}
              </IntroDescription>
            </IntroContent>
            <IntroImage />
          </IntroSection>
          
          <MenusTitle>{t('restaurant.menus.title')}</MenusTitle>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '2rem', 
            marginBottom: '2rem' 
          }}>
          {/* Sur mobile, les menus s'afficheront l'un en dessous de l'autre */}
            
            {/* Menu Brasserie */}
            <MenuCard>
              <MenuTitle>{brasserieMenus[1].title}</MenuTitle>
              
              <MenuSection>
                <MenuSectionTitle>{getMenuText(language, 'sections.starters')}</MenuSectionTitle>
                <MenuItems>
                  {getMenuText(language, 'dishes.brasserie.starters').map((starter, index) => (
                    <MenuItem key={index}>{starter}</MenuItem>
                  ))}
                </MenuItems>
              </MenuSection>
              
              <MenuSection>
                <MenuSectionTitle>{getMenuText(language, 'sections.mains')}</MenuSectionTitle>
                <MenuItems>
                  {getMenuText(language, 'dishes.brasserie.mains').map((main, index) => (
                    <MenuItem key={index}>{main}</MenuItem>
                  ))}
                </MenuItems>
              </MenuSection>
              
              <MenuSection>
                <MenuSectionTitle>{getMenuText(language, 'sections.desserts')}</MenuSectionTitle>
                <MenuItems>
                  {getMenuText(language, 'dishes.brasserie.desserts').map((dessert, index) => (
                    <MenuItem key={index}>{dessert}</MenuItem>
                  ))}
                </MenuItems>
              </MenuSection>
              
              <MenuPrice>{brasserieMenus[1].price}€</MenuPrice>
              
              <div style={{ marginTop: '20px' }}>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ padding: '8px 0' }}>{getMenuText(language, 'formuleOptions.option1')}</li>
                  <li style={{ padding: '8px 0' }}>{getMenuText(language, 'formuleOptions.option2')}</li>
                </ul>
                <div style={{ borderTop: '2px solid #4A6741', marginTop: '10px', paddingTop: '10px', fontWeight: 700, color: '#4A6741', fontSize: '1.3rem', textAlign: 'right' }}>{brasserieMenus[0].price}€</div>
              </div>
            </MenuCard>
            
            {/* Menu Restaurant */}
            <MenuCard>
              <MenuTitle>{getMenuText(language, 'dishes.restaurant.title')}</MenuTitle>
              
              <MenuSection>
                <MenuSectionTitle>{getMenuText(language, 'sections.starters')}</MenuSectionTitle>
                <MenuItems>
                  {getMenuText(language, 'dishes.restaurant.starters').map((starter, index) => (
                    <MenuItem key={index}>{starter}</MenuItem>
                  ))}
                </MenuItems>
              </MenuSection>
              
              <MenuSection>
                <MenuSectionTitle>{getMenuText(language, 'sections.mains')}</MenuSectionTitle>
                <MenuItems>
                  {getMenuText(language, 'dishes.restaurant.mains').map((main, index) => (
                    <MenuItem key={index}>{main}</MenuItem>
                  ))}
                </MenuItems>
              </MenuSection>
              
              <MenuSection>
                <MenuSectionTitle>{getMenuText(language, 'sections.desserts')}</MenuSectionTitle>
                <MenuItems>
                  {getMenuText(language, 'dishes.restaurant.desserts').map((dessert, index) => (
                    <MenuItem key={index}>{dessert}</MenuItem>
                  ))}
                </MenuItems>
              </MenuSection>
              
              <MenuPrice>{restaurantMenus[0].price}€</MenuPrice>
            </MenuCard>
          </div>
          
          <DownloadContainer>
            <DownloadButton href="/pdf/menu.pdf" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
              </svg>
              {getMenuText(language, 'footer.downloadMenu')}
            </DownloadButton>
          </DownloadContainer>

          <div className="text-center mt-4">
            <p>{getMenuText(language, 'footer.priceInfo')}</p>
            <p>{getMenuText(language, 'footer.dailyMenu')}</p>
          </div>
          
        </RestaurantContainer>
      </RestaurantSection>
    </>
  );
};

export default Restaurant;
